import pricingValidation from "../../../../components/modals/add-new-application-modal/pricing-validation";
import config from "../../../../config";
import * as Yup from "yup";

export const isBlankFieldValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number") return Number.isNaN(value) || value === 0;
  if (typeof value === "boolean") return value === false;
  if (Array.isArray(value)) {
    return value.length === 0 || value.every(isBlankFieldValue);
  }
  if (typeof value === "object") {
    const values = Object.values(value);
    return values.length === 0 || values.every(isBlankFieldValue);
  }
  return false;
};

export const CC_LOCKABLE_FIELDS = [
  "currently_accept_vdmax",
  "ways_accepting_cards",
  "sales_swiped",
  "sales_keyed",
  "internet",
  "recurring_bill_triel_radio",
  "monthly_max_sales",
  "avg_sale_amount",
  "high_sale_amount",
];

/**
 * Fields validated as a set: once any member is filled the rest stop counting
 * as "not started", since the percentage total and the amount comparisons only
 * mean anything across the whole group.
 */
export const VALIDATION_GROUPS = [
  ["internet", "sales_swiped", "sales_keyed"],
  ["monthly_max_sales", "avg_sale_amount", "high_sale_amount"],
];

/**
 * Returns `schema` with every top-level field skipped while it is blank, so
 * agents can save what they have. A blank field is still enforced when a
 * sibling in its validation group is filled, or when the CC section is locked —
 * locking asserts those values are final, and the merchant cannot fix them.
 */
export const skipBlankFields = (schema) =>
  Yup.object().shape(
    Object.fromEntries(
      Object.entries(schema?.fields ?? {}).map(([fieldName, fieldSchema]) => [
        fieldName,
        Yup.lazy((value, options) => {
          if (!isBlankFieldValue(value)) return fieldSchema;

          const parent = options?.parent;

          if (
            parent?.cc_processing_locked &&
            CC_LOCKABLE_FIELDS.includes(fieldName)
          ) {
            return fieldSchema;
          }

          const group = VALIDATION_GROUPS.find((fields) =>
            fields.includes(fieldName),
          );
          if (
            group &&
            group.some((member) => !isBlankFieldValue(parent?.[member]))
          ) {
            return fieldSchema;
          }

          return Yup.mixed().nullable().notRequired();
        }),
      ]),
    ),
  );

/**
 * Calculates the total of sales percentages from form values
 * @param {Object} values - Form values containing sales percentages
 * @param {string[]} fields - Array of field names to sum
 * @returns {number} Total percentage
 */
export const calculateSalesTotal = (values, fields) => {
  return fields.reduce((total, field) => total + Number(values[field] || 0), 0);
};

/**
 * Validates if sales percentages total equals 100%
 * @param {Object} values - Form values containing sales percentages
 * @param {string[]} fields - Array of field names to validate
 * @returns {boolean} True if total equals 100%, false otherwise
 */
export const validateSalesPercentages = (values, fields) => {
  const total = calculateSalesTotal(values, fields);
  return total === 100;
};

/**
 * Formats currency value with 2 decimal places
 * @param {number} value - Number to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  return Number(value || 0).toFixed(2);
};

/**
 * Validates if a number is within a reasonable range
 * @param {number} value - Value to validate
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {boolean} True if value is within range
 */
export const isWithinRange = (value, min, max) => {
  const num = Number(value);
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Returns the appropriate pricing validation object based on the pricing model
 * @param {string} pricingModel - The pricing model to get validation for
 * @param {string|null} organizationId - Optional organization ID for organization-specific validation
 * @returns {Object} The validation object for the specified pricing model
 */
export const getPricingValidation = (pricingModel, organizationId = null) => {
  if (pricingModel === "dual_pricing") {
    return getDualPricingValidation(organizationId);
  } else if (pricingModel === "surcharge") {
    return pricingValidation.pricingValidation.surcharge;
  } else if (pricingModel === "flat_rate") {
    return pricingValidation.pricingValidation.flat_rate;
  } else if (pricingModel === "tiered") {
    return pricingValidation.pricingValidation.tiered;
  } else if (pricingModel === "ic_plus") {
    return pricingValidation.pricingValidation.ic_plus;
  }

  return null;
};

/**
 * Returns the appropriate dual pricing validation object based on the organization ID
 * @param {string|null} organizationId - Optional organization ID for organization-specific validation
 * @returns {Object} The validation object for the specified organization ID
 */
export const getDualPricingValidation = (organizationId) => {
  if (organizationId == config.paymentcloud.PAYCHEX_ORG_ID) {
    return pricingValidation.pricingValidation.paychex_dual_pricing;
  } else {
    return pricingValidation.pricingValidation.dual_pricing;
  }
};
